import { useEffect, useState, useRef, useCallback } from 'react';

import { throttle } from '@/lib/utils';

const THREE_MINUTES = 1000 * 60 * 3;

type UseIdleReturn = {
  idle: boolean;
  timeRemaining: number;
  getTimeRemaining: () => number;
};

type UseIdleProps = {
  ms?: number;
  resetEventsNames?: string[];
  enableTimeTracking?: boolean;
};

export function useIdle({
  ms = THREE_MINUTES,
  resetEventsNames,
  enableTimeTracking = false,
}: UseIdleProps = {}): UseIdleReturn {
  const [idle, setIdle] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(ms);
  const timeoutIdRef = useRef<number | undefined>(undefined);
  const startTimeRef = useRef<number | undefined>(undefined);

  // Function to calculate time remaining on-demand
  const getTimeRemaining = useCallback(() => {
    if (!startTimeRef.current || idle) return 0;
    const elapsed = Date.now() - startTimeRef.current;
    return Math.max(0, ms - elapsed);
  }, [ms, idle]);

  useEffect(() => {
    const handleTimeout = () => {
      setIdle(true);
      if (enableTimeTracking) {
        setTimeRemaining(0);
      }
    };

    const startTimer = () => {
      setIdle(false);
      startTimeRef.current = Date.now();

      if (enableTimeTracking) {
        setTimeRemaining(ms);
      }

      // Clear existing timeout
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }

      // Set timeout for idle state - this only runs once per timer cycle
      timeoutIdRef.current = window.setTimeout(handleTimeout, ms);
    };

    const handleEvent = throttle(() => {
      startTimer();
    }, 500);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleEvent();
      }
    };

    // Initialize timer
    startTimer();

    window.addEventListener('mousemove', handleEvent);
    window.addEventListener('mousedown', handleEvent);
    window.addEventListener('resize', handleEvent);
    window.addEventListener('keydown', handleEvent);
    window.addEventListener('touchstart', handleEvent);
    window.addEventListener('wheel', handleEvent);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Listen for custom reset events if provided
    if (resetEventsNames && resetEventsNames.length > 0) {
      resetEventsNames.forEach(eventName => {
        window.addEventListener(eventName, handleEvent);
      });
    }

    return () => {
      window.removeEventListener('mousemove', handleEvent);
      window.removeEventListener('mousedown', handleEvent);
      window.removeEventListener('resize', handleEvent);
      window.removeEventListener('keydown', handleEvent);
      window.removeEventListener('touchstart', handleEvent);
      window.removeEventListener('wheel', handleEvent);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      // Remove custom reset event listeners if they were added
      if (resetEventsNames && resetEventsNames.length > 0) {
        resetEventsNames.forEach(eventName => {
          window.removeEventListener(eventName, handleEvent);
        });
      }

      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [ms, resetEventsNames, enableTimeTracking]);

  return {
    idle,
    timeRemaining: enableTimeTracking ? timeRemaining : getTimeRemaining(),
    getTimeRemaining,
  };
}
